(() => {
  if (customElements.get('collection-page-premium')) return;

  class CollectionPagePremium extends HTMLElement {
    connectedCallback() {
      this.onRootClick = this.handleRootClick.bind(this);
      this.onViewportScroll = this.queueNavigationUpdate.bind(this);
      this.onRootMutation = this.handleMutation.bind(this);

      this.addEventListener('click', this.onRootClick);
      this.observer = new MutationObserver(this.onRootMutation);
      this.observer.observe(this, { childList: true, subtree: true });

      this.refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onRootClick);
      this.detachViewport();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
    }

    handleMutation() {
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      this.translateInterface();
      this.attachViewport();
      this.queueNavigationUpdate();
    }

    attachViewport() {
      const nextViewport = this.querySelector('[data-collection-grid]');
      if (nextViewport === this.viewport) return;

      this.detachViewport();
      this.viewport = nextViewport;
      if (!this.viewport) return;

      this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueNavigationUpdate());
        this.resizeObserver.observe(this.viewport);
      }
    }

    detachViewport() {
      this.viewport?.removeEventListener('scroll', this.onViewportScroll);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.viewport = null;
    }

    handleRootClick(event) {
      const previousButton = event.target.closest('[data-collection-prev]');
      const nextButton = event.target.closest('[data-collection-next]');
      if (!previousButton && !nextButton) return;

      event.preventDefault();
      this.scrollProducts(previousButton ? -1 : 1);
    }

    scrollProducts(direction) {
      if (!this.viewport) return;

      const firstItem = this.viewport.querySelector('.up-collection-grid__item');
      const styles = getComputedStyle(this.viewport);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      const cardStep = firstItem ? firstItem.getBoundingClientRect().width + gap : 0;
      const amount = cardStep > 0 ? cardStep : Math.max(this.viewport.clientWidth * 0.82, 260);

      this.viewport.scrollBy({
        left: direction * amount,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    queueNavigationUpdate() {
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.updateNavigation());
    }

    updateNavigation() {
      const previousButton = this.querySelector('[data-collection-prev]');
      const nextButton = this.querySelector('[data-collection-next]');
      if (!this.viewport || (!previousButton && !nextButton)) return;

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const currentScroll = Math.max(0, this.viewport.scrollLeft);
      const canScroll = maxScroll > 4;

      if (previousButton) {
        previousButton.hidden = !canScroll;
        previousButton.disabled = !canScroll || currentScroll <= 4;
      }

      if (nextButton) {
        nextButton.hidden = !canScroll;
        nextButton.disabled = !canScroll || currentScroll >= maxScroll - 4;
      }
    }

    translateInterface() {
      const exactTranslations = new Map([
        ['Filter and sort', 'Filtrer et trier'],
        ['Filter', 'Filtrer'],
        ['Sort', 'Trier'],
        ['Sort by:', 'Trier par :'],
        ['Most relevant', 'En vedette'],
        ['Featured', 'En vedette'],
        ['Relevance', 'Pertinence'],
        ['Apply', 'Appliquer'],
        ['Clear', 'Effacer'],
        ['Remove all', 'Tout supprimer'],
        ['Reset', 'Réinitialiser'],
        ['Show more', 'Afficher plus'],
        ['Show less', 'Afficher moins'],
        ['From', 'De'],
        ['To', 'À'],
      ]);

      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((node) => {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, option')) return;
        const trimmed = node.nodeValue.trim();
        if (!exactTranslations.has(trimmed)) return;
        node.nodeValue = node.nodeValue.replace(trimmed, exactTranslations.get(trimmed));
      });

      this.querySelectorAll('select[name="sort_by"] option').forEach((option) => {
        const value = (option.value || '').trim().toLowerCase();
        const currentLabel = option.textContent.trim().toLowerCase();
        const labels = {
          manual: 'En vedette',
          featured: 'En vedette',
          relevance: 'Pertinence',
          'relevance-descending': 'Pertinence',
          'most-relevant': 'Pertinence',
          'best-selling': 'Meilleures ventes',
          'title-ascending': 'Nom, A à Z',
          'title-descending': 'Nom, Z à A',
          'price-ascending': 'Prix croissant',
          'price-descending': 'Prix décroissant',
          'created-ascending': 'Plus anciens',
          'date-ascending': 'Plus anciens',
          'created-descending': 'Nouveautés',
          'date-descending': 'Nouveautés',
        };

        let translatedLabel = labels[value];
        if (!translatedLabel && (currentLabel.includes('most relevant') || currentLabel.includes('relevance'))) {
          translatedLabel = 'Pertinence';
        } else if (!translatedLabel && currentLabel.includes('featured')) {
          translatedLabel = 'En vedette';
        }

        if (translatedLabel && option.textContent.trim() !== translatedLabel) {
          option.textContent = translatedLabel;
        }
      });

      this.querySelectorAll('.mobile-facets__open-label.medium-hide.large-up-hide').forEach((label) => {
        if (label.textContent !== 'Filtrer') label.textContent = 'Filtrer';
      });

      this.querySelectorAll('.mobile-facets__heading').forEach((heading) => {
        if (heading.textContent !== 'Filtrer et trier') heading.textContent = 'Filtrer et trier';
      });

      this.querySelectorAll('.facet-filters__label label').forEach((label) => {
        if (label.textContent !== 'Trier par :') label.textContent = 'Trier par :';
      });

      this.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach((element) => {
        const numbers = (element.textContent.match(/\d[\d\s.,]*/g) || [])
          .map((value) => value.replace(/\D/g, ''))
          .map((value) => Number.parseInt(value, 10))
          .filter(Number.isFinite);

        if (numbers.length >= 2) {
          const visible = numbers[0];
          const total = numbers[1];
          const label = `${visible} ${visible > 1 ? 'produits' : 'produit'} sur ${total}`;
          if (element.textContent.trim() !== label) element.textContent = label;
        } else if (numbers.length === 1) {
          const count = numbers[0];
          const label = `${count} ${count > 1 ? 'produits' : 'produit'}`;
          if (element.textContent.trim() !== label) element.textContent = label;
        }
      });
    }
  }

  customElements.define('collection-page-premium', CollectionPagePremium);
})();
